import axios from "axios";
import { Stream } from "src/models";
import { BrowserService } from "./browser.service";
import { PlayerMessageService, Player as PlayerType } from "./player";
import { SeoService } from "./seo.service";
import { StringService } from "./string.service";
import 'videojs-landscape-fullscreen'

export type Player = PlayerType;

export namespace PlayerService {

    //#region player

    export const makePlayer = async (): Promise<Player> => {

        //#region load dependencies

        const loadDependencies = async () => {

            const videojs = (await import(/* webpackChunkName: "player" */"video.js")).default;
            //@ts-ignore
            const qualityLevels = (await import(/* webpackChunkName: "player" */ "videojs-contrib-quality-levels")).default;
            if (!videojs.getPlugin("qualityLevels")) videojs.registerPlugin("qualityLevels", qualityLevels);
            //@ts-ignore
            const farakavQualitySelector = (await import(/* webpackChunkName: "player" */"farakav-quality-selector")).default;
            if (!videojs.getPlugin("farakavQualitySelector")) videojs.registerPlugin("farakavQualitySelector", farakavQualitySelector);
            //@ts-ignore
            const contribAds = (await import(/* webpackChunkName: "player" */"videojs-contrib-ads")).default;
            if (!videojs.getPlugin("ads")) videojs.registerPlugin("ads", contribAds);
            //@ts-ignore
            await import(/* webpackChunkName: "player" */"./player/ima3");
            //@ts-ignore
            const ima = (await import(/* webpackChunkName: "player" */"videojs-ima")).default;
            if (!videojs.getPlugin("ima")) videojs.registerPlugin("ima", ima);

            (await import(/* webpackChunkName: "player" */"./player/videojs-hlsjs-plugin")).default.registerSourceHandler(videojs);

            const dictionary: any = StringService.Dictionary() || {};
            const fa: any = (await import(/* webpackChunkName: "player" */"./player/fa.json"));
            const lang = { ...fa, ...dictionary };
            const addRepotersMenu = (await import(/* webpackChunkName: "player" */"./player/reporterMenu")).addRepotersMenu;
            const addEconomicButton = (await import(/* webpackChunkName: "player" */"./player/economicButton")).addEconomicButton;

            return [videojs, lang, addRepotersMenu, addEconomicButton];
        };

        //#endregion

        const [videojs, lang, addRepotersMenu, addEconomicButton] = await loadDependencies();
        let iframeClass = "icon-left";
        let adsLoaded = false;
        let adTimer: any;
        let skipButton: HTMLButtonElement | undefined = undefined;
        let player: videojs.VideoJsPlayer | undefined = undefined;

        //#region configuration

        const playerDefaultConfig: videojs.VideoJsPlayerOptions = {
            controls: true,
            fluid: false,
            autoplay: false,
            muted: true,
            languages: {
                fa: lang,
            },
            language: 'fa',
            liveui: true,
            html5: {
                nativeAudioTracks: false,
                nativeVideoTracks: false,
                vhs: {
                    debug: false,
                    overrideNative: true
                },
                hls: {
                    debug: false,
                    overrideNative: true
                },
            },
        };

        const adsDefaultConfig = {
            adLabel: 'تبلیغ',
            disableAdControls: false,
            disableCustomPlaybackForIOS10Plus: true,
            locale: "fa",
            adsRenderingSettings: {
                loadVideoTimeout: 15000,
                useStyledNonLinearAds: true
            },
            showCountdown: false,
            adsManagerLoadedCallback: () => {

                playVideo(false, true);

                //@ts-ignore
                player.ima.addEventListener(google.ima.AdEvent.Type.STARTED, () => setAdLoaded(false));
                //@ts-ignore
                player.ima.addEventListener(google.ima.AdEvent.Type.COMPLETE, () => playVideo(false, true));
                //@ts-ignore
                player.ima.addEventListener(google.ima.AdEvent.Type.SKIPPED, () => playVideo(false, true));
                //@ts-ignore
                player.ima.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, () => setAdLoaded(true));
                //@ts-ignore
                player.ima.addEventListener(google.ima.AdError.Type.AD_LOAD, () => setAdLoaded(true));
                //@ts-ignore
                player.ima.addEventListener(google.ima.AdError.Type.AD_PLAY, () => setAdLoaded(true));
                //@ts-ignore
                player.ima.addEventListener(google.ima.AdEvent.Type.LOG, () => setAdLoaded(true));
            }
        };

        const keyMap = [
            { key: ["space", "enter", "numpadenter"], action: () => togglePlay() },
            { key: ["keyf"], action: () => toggleFullScreen() },
            { key: ["keym"], action: () => toggleMute() },
            { key: ["arrowright"], action: () => seekForward() },
            { key: ["arrowleft"], action: () => seekBackward() },
            {
                key: ["^digit", "^numpad"], action: (code: string) => {
                    const digit = code.replace(/^numpad|digit/i, "");
                    if (!isNaN(+digit)) seekTo(+digit);
                }
            }
        ];

        const getVideoConfig = async (source: string, type: string, poster?: string): Promise<videojs.VideoJsPlayerOptions> => {
            const sourceType = type.toLowerCase() === "mp4" || type.toLowerCase() === "other" ?
                "video/mp4" : "application/x-mpegURL";

            const config: videojs.VideoJsPlayerOptions = {
                ...playerDefaultConfig,
                poster,
                sources: [{
                    src: source,
                    type: sourceType
                }],
            };

            try {
                const canAutoplay = (await import("can-autoplay")).default;
                const { result } = await canAutoplay.video({ muted: false, inline: true });
                if (result) config.muted = false;
            }
            catch { }

            return config;
        };

        //#endregion

        //#region player interactions

        const isLive = () => {

            //@ts-ignore
            return player?.el_.classList.contains("vjs-live") || (player?.$(".vjs-live-control") && !player.$(".vjs-live-control.vjs-hidden"));

        };

        const togglePlay = () => {
            if (!player) return false;

            player.paused() ? player.play() : player.pause();

            return true;
        };

        const volumeUp = () => {
            if (!player) return false;

            player.volume(player.volume() + .1);

            return true;
        };

        const volumeDown = () => {
            if (!player) return false;

            player.volume(player.volume() - .1);

            return true;
        };

        const seekForward = () => {

            if (!player || isLive()) return false;

            player.currentTime(player.currentTime() + 10);

            return true;
        };

        const seekBackward = () => {
            if (!player || isLive()) return false;

            player.currentTime(player.currentTime() - 10);

            return true;
        };

        const seekTo = (position: number) => {
            if (!player || isLive()) return false;

            player.currentTime(player.duration() * position / 10);

            return true;
        };

        const toggleMute = () => {
            if (!player) return false;

            player.muted(!player.muted());

            return true;
        };

        const toggleFullScreen = () => {
            if (!player) return false;

            player.isFullscreen() ? player.exitFullscreen() : player.requestFullscreen();

            return true;
        };

        const playVideo = async (muted: boolean = false, retry: boolean = true, retryUmuted: boolean = false) => {
            document.getElementById("custom-skip-button")?.remove();
            skipButton = undefined;
            if (adTimer) {
                clearInterval(adTimer);
                adTimer = undefined;
            }
            try {
                if (!player) return;
                player.muted(muted);
                await player.play();
            }
            catch {
                if (!retry) return;
                await playVideo(!retryUmuted, false, false);
            }
        };


        const exitFullScreen = () => {
            if (!player) return false;

            if (player.isFullscreen()) player.exitFullscreen();
        };

        const exitPictureInPicture = () => {
            if (!player) return false;

            try {
                //@ts-ignore
                if (document.pictureInPictureElement) {
                    //@ts-ignore
                    document.exitPictureInPicture();
                }
            }
            catch { }
        };

        const enterFullScreen = () => {
            if (!player) return false;

            if (!player.isFullscreen()) player.requestFullscreen();
        };

        const isFullScreen = () => player ? player.isFullscreen() : false;

        const disposePlayer = (onDispose?: Function) => {

            if (onDispose) onDispose();

            if (player) {
                player.dispose();
                player = undefined;
                document.onkeydown = null;
            }

            skipButton = undefined;
            if (adTimer) {
                clearInterval(adTimer);
                adTimer = undefined;
            }

        };

        const addMobileUnmute = () => {

            if (!player) return;

            let btn = document.querySelector<HTMLButtonElement>(".farakav-unmute-btn");
            if (btn) return;

            btn = document.createElement("button");
            btn.classList.add("farakav-unmute-btn");
            btn.onclick = toggleMute;

            const icon = document.createElement("i");
            icon.classList.add("vjs-icon-volume-mute");
            btn.append(icon);

            player.el().append(btn);

        };


        //#endregion

        //#region  player initilaztion

        const addAntenLogo = () => {
            if (!player) return;

            if (document.querySelectorAll(".vjs-anten").length) return;

            const img = player.controlBar.addChild('Component', {}, 9);
            img.addClass("vjs-anten");
        };

        const addQualityTitle = () => {

            const btn = document.querySelector(".vjs-quality-selector button");
            if (!btn) return;

            btn.setAttribute("title", "کیفیت");

        };

        const remapHotkeys = () => {

            document.querySelectorAll(".vjs-control").forEach((element) => {
                if (element instanceof HTMLElement) {
                    element.onclick = element.blur;
                }
            });

            document.onkeydown = (e: KeyboardEvent) => {

                if (!player) return true;

                let mustCapture = false;

                if (e.target) {
                    const target = e.target as Element;

                    mustCapture =
                        !(/^(input|textarea|select|button|submit)$/i).test(target.tagName) &&
                        !target.classList.contains("modal-component") && !target.closest(".modal-component");
                }

                if (!mustCapture) return true;

                const code = e.code;

                const keyAction = keyMap.find((x) => x.key.some((y) => new RegExp(y, 'i').test(code)));

                if (keyAction) {
                    keyAction.action(code);
                    return false;
                }

            };

        };

        const addSkipButton = () => {

            //@ts-ignore
            const adManager = player?.ima.getAdsManager();
            let skipffset = adManager.getCurrentAd().g.skipTimeOffset;
            if (!skipffset) return;

            skipButton = document.createElement("button");
            skipButton.classList.add("custom-skip-button");

            skipButton.id = "custom-skip-button";
            //@ts-ignore
            if (adManager.getCurrentAd().isSkippable()) {
                if (iframeClass) {
                    document.querySelector(".video-js iframe")?.classList.add(iframeClass);
                }
                //@ts-ignore
                player.contentEl().append(skipButton);
            }

            skipButton.onclick = function () {
                if (adManager.getAdSkippableState()) {
                    adManager.skip();
                }
            };

            adTimer = setInterval(function () {
                if (skipffset > 0) {
                    skipffset = adManager.getCurrentAd().g.skipTimeOffset - adManager.getCurrentAd().g.duration + adManager.getRemainingTime();
                    skipffset = Math.floor(skipffset);
                    if (skipButton) {
                        skipButton.innerText = "تماشای ویدیو " + (skipffset || "");
                        if (!skipffset) skipButton.classList.add("no-countdown");
                    }
                }
                else {
                    if (skipButton) {
                        skipButton.innerText = "تماشای ویدیو";
                        clearInterval(adTimer);
                        skipButton.classList.add("no-countdown");
                    }
                }


            }, 500);
        };

        const setAdLoaded = (play: boolean) => {
            adsLoaded = true;
            document.querySelector<HTMLElement>('.vjs-control-bar')!.style.visibility = 'visible';
            document.querySelector<HTMLElement>('.vjs-big-play-button')!.style.visibility = 'visible';
            document.querySelector('.video-js')!.classList.remove('vjs-waiting');
            if (play) playVideo(false, true);
        };


        const attachEvents = (onPlay?: Function, onPause?: Function, onReady?: Function, onEnded?: Function, addLogo?: boolean) => {

            if (!player) return;

            //@ts-ignore
            player.landscapeFullscreen({
              fullscreen: {
                enterOnRotate: true,
                exitOnRotate: true,
                alwaysInLandscapeMode: true,
                iOS: false,
              },
            })

            if (onPlay) {
                player.on("play", (e) => {
                    if (!adsLoaded) {
                        e.preventDefault();
                        player?.pause();


                        document.querySelector<HTMLElement>('.vjs-control-bar')!.style.visibility = 'hidden';
                        document.querySelector<HTMLElement>('.vjs-big-play-button')!.style.visibility = 'hidden';
                        document.querySelector('.video-js')!.classList.add('vjs-waiting');

                        return;
                    }
                    if (player?.muted()) {
                        player?.addClass("vjs-muted");
                    }
                    onPlay();
                });
            }

            if (onPause) {
                player.on("pause", () => onPause());
                player.on("error", () => onPause());
            }

            player.on("ended", () => {
                if (player && player.isFullscreen()) {
                    player.exitFullscreen();
                }

                if (onEnded) onEnded();
            });

            player.ready(() => {

                if (BrowserService.isiPad || BrowserService.isIOS) {
                    player?.el().classList.add("ios-player");
                }

                if (addLogo) addAntenLogo();
                addMobileUnmute();

                remapHotkeys();

                if (onReady) onReady();

                try {
                    //@ts-ignore
                    player.qualityLevels().dispose();
                    //@ts-ignore
                    player.qualityLevels().trigger("addqualitylevel");
                }
                catch { }

                setTimeout(addQualityTitle);
            });

            player.on("volumechange", () => {
                if (player?.muted()) {
                    player?.addClass("vjs-muted");
                }
                else {
                    player?.removeClass("vjs-muted");
                }
            });

            player.on("error", () => exitFullScreen());
            player.on("adserror", () => setAdLoaded(true));
            player.on('ads-ad-started', addSkipButton);
        };

        const getAdsResponse = async (adTagUrl?: string) => {
            try {
                if (!adTagUrl) {
                    adsLoaded = true;
                    return "";
                }
                const res = await axios.get(adTagUrl, { responseType: "text", withCredentials: true });
                const adsResponse = res.data;
                const parser = new DOMParser();
                const dom = parser.parseFromString(adsResponse, "application/xml");
                const icon = dom.querySelectorAll("Icon");
                iframeClass = "icon-left";
                if (icon.length > 0) {
                    const xPosition = icon[0].getAttribute("xPosition");
                    if (!xPosition || xPosition.toLowerCase() === "left") {
                        iframeClass = "icon-left";
                    }
                    else {
                        iframeClass = "icon-right";
                    }

                }
                return adsResponse;

            } catch {
                adsLoaded = true;
                return "";
            }
        };

        const initPlayer = async (
            element: HTMLVideoElement, source: string, type: string, poster?: string, adTagUrl?: string,
            streams?: Stream[], currentStream?: Stream, onPlay?: Function, onPause?: Function, onReady?: Function, onEnded?: Function,
            addLogo?: boolean, economic?: boolean, economicalEnabled?: boolean) => {

            if (!element) return;

            await loadDependencies();

            const config = await getVideoConfig(source, type, poster);

            const adsResponse = await getAdsResponse(adTagUrl);

            player = videojs(element, config);

            attachEvents(onPlay, onPause, onReady, onEnded, addLogo);

            if (player) {
                //@ts-ignore
                player.farakavQualitySelector();

                if (streams && streams.filter((item) => item.name || item.familyName || item.image).length) {
                    try {
                        addRepotersMenu(videojs, player, streams, currentStream);
                    }
                    catch { }
                }

                if (economicalEnabled) {
                    try {
                        addEconomicButton(videojs, player, economic, currentStream);
                    }
                    catch { }
                }
            }

            SeoService.setMediaMetadata(poster, currentStream);

            if (!!adsResponse) initAds(adsResponse);
            else player?.on("ready", () => {
                setTimeout(playVideo);
            });

        };

        const changeStream = async (source: string, type: string, adTagUrl?: string, poster?: string, currentStream?: Stream,
            onPlay?: Function, onPause?: Function, onReady?: Function, onEnded?: Function) => {

            if (!player) return;

            const sourceType = type.toLowerCase() === "mp4" || type.toLowerCase() === "other" ?
                "video/mp4" : "application/x-mpegURL";

            player.src({
                src: source,
                type: sourceType
            });

            attachEvents(onPlay, onPause, onReady, onEnded);

            SeoService.setMediaMetadata(poster, currentStream);

            if (!!adTagUrl) {

                const adsResponse = await getAdsResponse(adTagUrl);

                //@ts-ignore
                if (typeof player.ima === "function") {
                    initAds(adsResponse);
                }
                else {
                    //@ts-ignore
                    player.ima.changeAdTag(adTagUrl);
                    //@ts-ignore
                    player.ima.requestAds();
                    playVideo(player.muted(), true, true);
                }
            }
            else playVideo(player.muted(), true, true);


        };


        const initAds = (adsResponse: string) => {

            if (!player) return;

            const config = {
                ...adsDefaultConfig,
                adsResponse
            };

            //@ts-ignore
            player.ima(config);

            //@ts-ignore
            player.ima.initializeAdDisplayContainer();

        };

        //#endregion

        return ({
            togglePlay,
            volumeUp,
            volumeDown,
            seekForward,
            seekBackward,
            seekTo,
            toggleMute,
            toggleFullScreen,
            exitFullScreen,
            enterFullScreen,
            isFullScreen,
            disposePlayer,
            initPlayer,
            initAds,
            changeStream,
            exitPictureInPicture
        });

    };

    //#endregion

    //#region program status handler

    export const setProgramAvailabilityByInfo = PlayerMessageService.setProgramAvailabilityByInfo;
    export const setProgramAvailabilityByStatus = PlayerMessageService.setProgramAvailabilityByStatus;
    export const setTvAvailabilityByStatus = PlayerMessageService.setTvAvailabilityByStatus;
    export const setProgramLocked = PlayerMessageService.setProgramLocked;

    //#endregion

}
